import React, { useState } from 'react'
import { useUpdateTaskStatus, useUpdateTaskResources } from '../../hooks/useTasks'
import type { Task } from '../../hooks/useTasks'
import { useBoQ } from '../../hooks/useBoQ'
import { useCurvaS } from '../../hooks/useCurvaS'
import { TaskResourceForm } from '../forms/TaskResourceForm'
import { RescheduleImpactCalculator } from '../forms/RescheduleImpactCalculator'

interface TaskCardProps {
  projectId: number
  task: Task
  onStatusChange?: () => void
}

export const TaskCard: React.FC<TaskCardProps> = ({ projectId, task, onStatusChange }) => {
  const [showResourceForm, setShowResourceForm] = useState(false)
  const [showRescheduleForm, setShowRescheduleForm] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  
  const { data: boqData } = useBoQ(projectId)
  const { data: curvaData } = useCurvaS(projectId)
  
  const { mutate: updateTaskStatus } = useUpdateTaskStatus(projectId)
  const { mutate: updateTaskResources } = useUpdateTaskResources(projectId)
  
  const handleStatusChange = (newStatus: Task['status']) => {
    updateTaskStatus(
      { taskId: task.id, status: newStatus },
      {
        onSuccess: () => {
          onStatusChange?.()
        }
      }
    )
  }
  
  const handleRescheduleApply = (newStartDate: string, newEndDate: string) => {
    // In a real implementation, this would update the task dates
    console.log('Applying reschedule:', { newStartDate, newEndDate })
    // For now, just show the impact was applied
    alert('Task rescheduled successfully!')
  }
  
  const getStatusBadge = (status: Task['status']) => {
    const statusConfig = {
      planned: { bg: 'bg-gray-100', text: 'text-gray-800', icon: '⏳' },
      in_progress: { bg: 'bg-blue-100', text: 'text-blue-800', icon: '🔄' },
      completed: { bg: 'bg-green-100', text: 'text-green-800', icon: '✅' },
      delayed: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: '⏰' }
    }
    
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusConfig[status].bg} ${statusConfig[status].text}`}>
        {statusConfig[status].icon} {status.replace('_', ' ')}
      </span>
    )
  }
  
  return (
    <>
      <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm hover:shadow-md transition-shadow">
        <div className="flex justify-between items-start mb-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <h3 className="font-medium text-gray-900 truncate" title={task.taskName}>
                {task.taskName}
              </h3>
              {getStatusBadge(task.status)}
            </div>
            
            <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
              <span className="flex items-center gap-1">
                📅 {new Date(task.startDate).toLocaleDateString()} - {new Date(task.endDate).toLocaleDateString()}
              </span>
              <span className="flex items-center gap-1">
                ⏱️ {task.duration} days
              </span>
              <span className="flex items-center gap-1">
                📊 {task.progress?.toFixed(1) || 0}%
              </span>
            </div>
            
            {task.boqItemId && boqData && (
              <div className="text-sm text-gray-500 mb-3">
                📋 BoQ Item ID: {task.boqItemId}
                {boqData.categories.flatMap(cat => cat.items).find(item => item.id === task.boqItemId) && (
                  <div className="mt-1 text-xs">
                    <div>Item: {boqData.categories.flatMap(cat => cat.items).find(item => item.id === task.boqItemId)?.itemName}</div>
                    <div>Qty: {boqData.categories.flatMap(cat => cat.items).find(item => item.id === task.boqItemId)?.quantity} {boqData.categories.flatMap(cat => cat.items).find(item => item.id === task.boqItemId)?.unit}</div>
                    <div>Unit Price: Rp {boqData.categories.flatMap(cat => cat.items).find(item => item.id === task.boqItemId)?.unitPrice?.toLocaleString()}</div>
                  </div>
                )}
              </div>
            )}
            
            {task.plannedWeight && (
              <div className="text-sm text-blue-600 mb-3">
                📊 Planned Weight: {task.plannedWeight}%
                {task.actualWeight && (
                  <div className="text-sm text-green-600">
                    ✅ Actual Weight: {task.actualWeight}%
                  </div>
                )}
              </div>
            )}
            
            {task.resources && (
              <div className="text-sm text-gray-600 space-y-1 mb-4">
                {task.resources.personil && (
                  <div className="flex items-center gap-1">
                    👥 {task.resources.personil} personil
                  </div>
                )}
                {task.resources.equipment && task.resources.equipment.length > 0 && (
                  <div className="flex items-center gap-1">
                    🔧 {task.resources.equipment.join(', ')}
                  </div>
                )}
                {task.resources.budget && (
                  <div className="flex items-center gap-1">
                    💰 Rp {task.resources.budget.toLocaleString()}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => handleStatusChange('planned')}
            disabled={task.status === 'planned'}
            className={`px-3 py-1 text-xs rounded-md ${task.status === 'planned' ? 'bg-gray-200 text-gray-700' : 'bg-gray-100 text-gray-800 hover:bg-gray-200'}`}
          >
            Planned
          </button>
          <button
            onClick={() => handleStatusChange('in_progress')}
            disabled={task.status === 'in_progress'}
            className={`px-3 py-1 text-xs rounded-md ${task.status === 'in_progress' ? 'bg-blue-200 text-blue-800' : 'bg-blue-100 text-blue-800 hover:bg-blue-200'}`}
          >
            In Progress
          </button>
          <button
            onClick={() => handleStatusChange('completed')}
            disabled={task.status === 'completed'}
            className={`px-3 py-1 text-xs rounded-md ${task.status === 'completed' ? 'bg-green-200 text-green-800' : 'bg-green-100 text-green-800 hover:bg-green-200'}`}
          >
            Completed
          </button>
          <button
            onClick={() => handleStatusChange('delayed')}
            disabled={task.status === 'delayed'}
            className={`px-3 py-1 text-xs rounded-md ${task.status === 'delayed' ? 'bg-yellow-200 text-yellow-800' : 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'}`}
          >
            Delayed
          </button>
          <button
            onClick={() => setShowResourceForm(true)}
            className="px-3 py-1 text-xs bg-purple-100 text-purple-800 rounded-md hover:bg-purple-200"
          >
            Assign Resources
          </button>
          <button
            onClick={() => setShowRescheduleForm(true)}
            className="px-3 py-1 text-xs bg-orange-100 text-orange-800 rounded-md hover:bg-orange-200"
          >
            Reschedule
          </button>
        </div>
      </div>
      
      {showResourceForm && (
        <TaskResourceForm
          projectId={projectId}
          task={task}
          onClose={() => setShowResourceForm(false)}
          onSuccess={onStatusChange}
        />
      )}
      
      {showRescheduleForm && (
        <RescheduleImpactCalculator
          projectId={projectId}
          task={task}
          onClose={() => setShowRescheduleForm(false)}
          onApply={handleRescheduleApply}
        />
      )}
    </>
  )
}

TaskCard.displayName = 'TaskCard'